import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Briefcase, MapPin, CheckCircle2, ChevronDown, Building2, Search, X,
} from 'lucide-react';
import type { Project } from '../../types/appShared';

interface StatusCfgEntry {
    color: string;
    bg: string;
}

const STATUS_CFG: Record<string, StatusCfgEntry> = {
    Active: { color: '#10b981', bg: 'rgba(16,185,129,.12)' },
    Completed: { color: '#3b82f6', bg: 'rgba(59,130,246,.12)' },
    'On Hold': { color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
    Cancelled: { color: '#ef4444', bg: 'rgba(239,68,68,.12)' },
};

interface ProjectSelectorProps {
    currentProjectId: string | null;
    projects: Record<string, Project>;
    canViewProject: (projectId: string) => boolean;
    showToast?: (message: string, type?: string) => void;
    onSwitchProject: (projectId: string) => void;
    switchingProject?: boolean;
}

const ProjectSelector = ({ currentProjectId, projects, canViewProject, onSwitchProject, switchingProject }: ProjectSelectorProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const wrapRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const currentProject = currentProjectId ? projects[currentProjectId] : undefined;

    const allProjects = Object.values(projects).filter(p => canViewProject(p.id));

    // Filter by status + search
    const filteredProjects = allProjects
        .filter(p => filterStatus === 'all' || p.status === filterStatus)
        .filter(p =>
            (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.client || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.shortName || p.short_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.location || '').toLowerCase().includes(searchQuery.toLowerCase())
        );

    // Group by status when no search/filter
    const groupedProjects = useMemo(() => {
        if (searchQuery.trim() || filterStatus !== 'all') return null;
        const order = ['Active', 'On Hold', 'Completed', 'Cancelled'];
        const groups: Record<string, Project[]> = {};
        order.forEach(s => {
            const items = filteredProjects.filter(p => p.status === s);
            if (items.length) groups[s] = items;
        });
        return groups;
    }, [filteredProjects, searchQuery, filterStatus]);

    // Stats
    const statusCounts = useMemo(() => {
        const counts: Record<string, number> = { all: allProjects.length };
        ['Active', 'Completed', 'On Hold', 'Cancelled'].forEach(s => {
            counts[s] = allProjects.filter(p => p.status === s).length;
        });
        return counts;
    }, [allProjects]);

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Focus search on open
    useEffect(() => {
        if (isOpen) setTimeout(() => searchRef.current?.focus(), 80);
        else { setSearchQuery(''); setFilterStatus('all'); }
    }, [isOpen]);

    // منطق تبديل المشروع (تصفير البيانات + fetch) مركزي في App
    // ومتاح هنا عبر prop واحد onSwitchProject، بدل ما يتكرر جوه الكومبوننت دا كمان.
    const handleProjectSwitch = (newProjectId: string) => {
        if (newProjectId === currentProjectId) { setIsOpen(false); return; }
        onSwitchProject(newProjectId);
        setIsOpen(false);
        setSearchQuery('');
    };

    const curCfg = currentProject?.status ? STATUS_CFG[currentProject.status] : undefined;

    const renderItem = (project: Project) => {
        const isActive = project.id === currentProjectId;
        const cfg = (project.status && STATUS_CFG[project.status]) || STATUS_CFG.Active;
        const name = project.shortName || project.short_name;
        return (
            <motion.button
                key={project.id}
                onClick={() => handleProjectSwitch(project.id)}
                whileHover={{ backgroundColor: 'rgba(59,130,246,.07)' }}
                style={{
                    width: '100%', padding: '9px 14px', textAlign: 'left',
                    border: 'none', borderBottom: '1px solid rgba(255,255,255,.04)',
                    background: isActive ? 'rgba(59,130,246,.08)' : 'transparent',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    fontFamily: 'inherit',
                }}
            >
                {/* Logo or icon */}
                <div style={{
                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: project.logo_url ? '#fff' : cfg.bg,
                    border: `1px solid ${cfg.color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', padding: project.logo_url ? 3 : 0,
                }}>
                    {project.logo_url
                        ? <img src={project.logo_url} alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
                        : <Briefcase size={13} color={cfg.color} />
                    }
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{
                            fontSize: 12, fontWeight: isActive ? 800 : 600,
                            color: isActive ? '#f1f5f9' : '#cbd5e1',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{name}</span>
                        <span style={{
                            width: 5, height: 5, borderRadius: '50%',
                            background: cfg.color, flexShrink: 0,
                        }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#475569' }}>
                        {project.client && (
                            <span style={{
                                overflow: 'hidden', textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap', maxWidth: 140,
                            }}>{project.client}</span>
                        )}
                        {project.location && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                                <MapPin size={9} /> {project.location}
                            </span>
                        )}
                    </div>
                </div>

                {isActive && <CheckCircle2 size={14} color="#3b82f6" style={{ flexShrink: 0 }} />}
            </motion.button>
        );
    };

    return (
        <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>

            {/* ── Trigger ── */}
            <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => setIsOpen(!isOpen)}
                disabled={switchingProject}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 10, padding: '9px 13px',
                    borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
                    background: 'rgba(255,255,255,.04)',
                    border: `1.5px solid ${isOpen ? 'rgba(59,130,246,.35)' : 'rgba(255,255,255,.08)'}`,
                    transition: 'all .18s', color: 'inherit',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    {/* Logo/icon */}
                    <div style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        background: currentProject?.logo_url ? '#fff'
                            : curCfg ? curCfg.bg : 'rgba(59,130,246,.12)',
                        border: `1px solid ${curCfg ? curCfg.color + '30' : 'rgba(59,130,246,.2)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', padding: currentProject?.logo_url ? 3 : 0,
                    }}>
                        {currentProject?.logo_url
                            ? <img src={currentProject.logo_url} alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
                            : <Building2 size={15} color={curCfg?.color || '#3b82f6'} />
                        }
                    </div>

                    <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: 10, color: '#475569', fontWeight: 600, margin: 0 }}>
                            {switchingProject ? 'Switching...' : 'Current Project'}
                        </p>
                        <p style={{
                            fontSize: 13, fontWeight: 800, color: '#f1f5f9', margin: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {switchingProject ? '…' : (currentProject?.shortName || currentProject?.short_name || 'Select Project')}
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {!switchingProject && statusCounts.Active > 0 && (
                        <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
                            background: 'rgba(16,185,129,.1)', color: '#34d399',
                        }}>{statusCounts.Active} active</span>
                    )}
                    <ChevronDown size={14} color="#475569" style={{
                        transition: 'transform .2s',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }} />
                </div>
            </motion.button>

            {/* ── Dropdown ── */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: .97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: .97 }}
                        transition={{ duration: .15 }}
                        style={{
                            position: 'absolute', top: 'calc(100% + 8px)',
                            left: 0, right: 0, zIndex: 999,
                            background: '#0d1117',
                            border: '1px solid rgba(255,255,255,.1)',
                            borderRadius: 14, overflow: 'hidden',
                            boxShadow: '0 24px 48px rgba(0,0,0,.6)',
                        }}
                    >
                        {/* Search */}
                        <div style={{
                            padding: 10, borderBottom: '1px solid rgba(255,255,255,.06)',
                            background: 'rgba(255,255,255,.02)',
                        }}>
                            <div style={{ position: 'relative' }}>
                                <Search size={13} style={{
                                    position: 'absolute', left: 9, top: '50%',
                                    transform: 'translateY(-50%)', color: '#475569',
                                }} />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Search projects, clients, locations…"
                                    style={{
                                        width: '100%', padding: '7px 30px 7px 28px',
                                        borderRadius: 8, border: '1.5px solid rgba(255,255,255,.08)',
                                        background: 'rgba(255,255,255,.04)', color: '#e2e8f0',
                                        fontSize: 12, fontFamily: 'inherit', outline: 'none',
                                        boxSizing: 'border-box',
                                    }}
                                    onFocus={e => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,.08)'}
                                />
                                {searchQuery && (
                                    <button onClick={() => setSearchQuery('')} style={{
                                        position: 'absolute', right: 8, top: '50%',
                                        transform: 'translateY(-50%)', background: 'none',
                                        border: 'none', cursor: 'pointer', color: '#475569',
                                        display: 'flex', alignItems: 'center',
                                    }}><X size={11} /></button>
                                )}
                            </div>
                        </div>

                        {/* Filter bar */}
                        <div style={{
                            display: 'flex', gap: 5, padding: '8px 10px',
                            borderBottom: '1px solid rgba(255,255,255,.06)',
                            overflowX: 'auto', scrollbarWidth: 'none',
                        }}>
                            {['all', 'Active', 'Completed', 'On Hold', 'Cancelled'].map(s => (
                                <button key={s}
                                    onClick={() => setFilterStatus(s)}
                                    style={{
                                        padding: '3px 9px', borderRadius: 99, fontSize: 11,
                                        fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                                        border: `1.5px solid ${filterStatus === s ? '#3b82f6' : 'rgba(255,255,255,.08)'}`,
                                        background: filterStatus === s ? 'rgba(59,130,246,.12)' : 'transparent',
                                        color: filterStatus === s ? '#60a5fa' : '#475569',
                                        fontFamily: 'inherit', transition: 'all .15s',
                                    }}>
                                    {s === 'all' ? `All (${statusCounts.all})` : `${s} (${statusCounts[s] || 0})`}
                                </button>
                            ))}
                        </div>

                        {/* List */}
                        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                            {filteredProjects.length === 0 ? (
                                <div style={{
                                    padding: '28px 16px', textAlign: 'center', color: '#475569',
                                }}>
                                    <p style={{ fontSize: 13, fontWeight: 600 }}>
                                        {searchQuery ? `No results for "${searchQuery}"` : 'No projects'}
                                    </p>
                                </div>
                            ) : groupedProjects ? (
                                Object.entries(groupedProjects).map(([status, items]) => (
                                    <div key={status}>
                                        <div style={{
                                            padding: '6px 14px 3px', fontSize: 10, fontWeight: 700,
                                            color: STATUS_CFG[status]?.color || '#475569',
                                            textTransform: 'uppercase', letterSpacing: '.08em',
                                            borderBottom: '1px solid rgba(255,255,255,.04)',
                                            background: 'rgba(255,255,255,.01)',
                                        }}>
                                            ● {status} — {items.length}
                                        </div>
                                        {items.map(renderItem)}
                                    </div>
                                ))
                            ) : (
                                filteredProjects.map(renderItem)
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '7px 14px', borderTop: '1px solid rgba(255,255,255,.06)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: 'rgba(255,255,255,.01)',
                        }}>
                            <span style={{ fontSize: 10, color: '#334155' }}>
                                {filteredProjects.length} of {allProjects.length} projects
                            </span>
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} style={{
                                    fontSize: 10, color: '#3b82f6', background: 'none',
                                    border: 'none', cursor: 'pointer', fontWeight: 600,
                                    fontFamily: 'inherit',
                                }}>Clear</button>
                            )}
                        </div>

                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ProjectSelector;
